import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Plus, MoreVertical, Calendar, Menu } from 'lucide-react';
import dashboardService from './api.js';
import { isAdmin, isAdvisor, isInvestmentBanker } from '../auth/services/authService';
import SlimClientProfile from './components/clientProfile/SlimClientProfile';
import Widget from './components/widget/index.js';
import TabNavigation from './components/tabNavigation';
import AddWidgetModal from './components/modals/AddWidgetModal';
import AddTabModal from './components/modals/AddTabModal';
import TranscriptionWidget from './components/transcriptionWidget/index.js';
import WealthChatBot from '../wealthChatbot/WealthChatbot.js';
import { PPT_BACKGROUND, PPT_LOGO } from '../../constants/pptConstants.js';
import WorkflowVisualizer from './components/widget/WorkflowVisualizer';
import Masonry from 'react-masonry-css';

const COL_STORAGE_KEY = 'preCallWidgetColMap';
const getStoredColMap = () => { try { return JSON.parse(localStorage.getItem(COL_STORAGE_KEY) || '{}'); } catch { return {}; } };
const saveColAssignment = (widgetId, colIdx) => { const m = getStoredColMap(); m[widgetId] = colIdx; localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(m)); };
const removeColAssignment = (widgetId) => { const m = getStoredColMap(); delete m[widgetId]; localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(m)); };

const ORDER_STORAGE_KEY = 'preCallWidgetOrder';
const getStoredOrder = () => { try { return JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) || '[]'); } catch { return []; } };
const saveStoredOrder = (order) => { localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order)); };

// Floating Actions Menu
const ActionsMenu = ({ onExportPPT, exportLoading, onAddWidget, selectedTab, widgetContents }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 focus:outline-none"
        title="Actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-50 overflow-hidden">
          <button
            onClick={() => { onExportPPT(); setOpen(false); }}
            disabled={exportLoading || Object.keys(widgetContents).length === 0 || !selectedTab}
            className="w-full text-left px-4 py-3 text-sm text-[#374151] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2.5 transition-all duration-200 hover:text-primary"
            style={{ lineHeight: '1.5' }}
          >
            <Download size={14} className="text-primary shrink-0" />
            {exportLoading ? 'Exporting...' : 'Export to PPT'}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="w-full text-left px-4 py-3 text-sm text-[#374151] hover:bg-gray-50 flex items-center gap-2.5 transition-all duration-200 hover:text-primary"
            style={{ lineHeight: '1.5' }}
          >
            <Calendar size={14} className="text-primary shrink-0" />
            Schedule Meeting
          </button>
          <div className="border-t border-gray-100 mx-3" />
          <button
            onClick={() => { onAddWidget(); setOpen(false); }}
            disabled={!selectedTab}
            className="w-full text-left px-4 py-3 text-sm text-[#374151] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2.5 transition-all duration-200 hover:text-primary"
            style={{ lineHeight: '1.5' }}
          >
            <Plus size={14} className="text-primary shrink-0" />
            Add Widget
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard2 = ({ customerId: customerIdProp } = {}) => {
  const [searchParams] = useSearchParams();
  const customerId = customerIdProp || searchParams.get('customerId') || undefined;
  const [widgetContents, setWidgetContents] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showTabPopup, setShowTabPopup] = useState(false);
  const [useCases, setUseCases] = useState([]);
  const [tools, setTools] = useState([]);
  const [selectedUseCase, setSelectedUseCase] = useState('tst1');
  const [exportLoading, setExportLoading] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [newTabName, setNewTabName] = useState('');

  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditModalData, setAuditModalData] = useState({ content: '', tool: '', userId: '', runId: null, loading: false });
  const [activeModalTab, setActiveModalTab] = useState('audit');
  const [graphData, setGraphData] = useState(null);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [graphError, setGraphError] = useState(null);
  const [widgetsLoading, setWidgetsLoading] = useState({});
  const [allWidgetsLoaded, setAllWidgetsLoaded] = useState(false);
  const [widgetColMap, setWidgetColMap] = useState({});
  const [widgetOrder, setWidgetOrder] = useState(() => getStoredOrder());
  const [numCols, setNumCols] = useState(() => window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
  const columnsRef = useRef([]);

  const [newWidget, setNewWidget] = useState({
    title: '', widgetType: 'text', tool: '', toolId: '',
    endpoint: '', prompt: '', visualPrompt: '', tabId: '', hasAvatar: false
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const initialFetchDone = useRef(false);
  const allWidgetsLoadedRef = useRef(false);
  const navigate = useNavigate();

  // Loading tracking
  useEffect(() => {
    setWidgetsLoading({});
    setAllWidgetsLoaded(false);
    allWidgetsLoadedRef.current = false;
  }, [selectedTab, selectedClient?.id]);

  useEffect(() => {
    if (widgets.length > 0 && !allWidgetsLoadedRef.current) {
      const currentTabWidgets = widgets.filter(w => w.tabId === selectedTab);
      if (currentTabWidgets.length === 0) return;
      const allIds = currentTabWidgets.map(w => w.id);
      const tracked = Object.keys(widgetsLoading);
      if (allIds.every(id => tracked.includes(id))) {
        const allLoaded = allIds.every(id => widgetsLoading[id] === false);
        if (allLoaded && !allWidgetsLoaded) {
          setAllWidgetsLoaded(true);
          allWidgetsLoadedRef.current = true;
        }
      }
    }
  }, [widgetsLoading, widgets, selectedTab, allWidgetsLoaded]);

  const handleWidgetLoadingChange = useCallback((widgetId, isLoading) => {
    setWidgetsLoading(prev => {
      if (prev[widgetId] === isLoading) return prev;
      return { ...prev, [widgetId]: isLoading };
    });
  }, []);

  useEffect(() => {
    const update = () => setNumCols(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    setUserId(sessionStorage.getItem('user_id') || 'U1757535255');
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await dashboardService.fetchUseCases();
        if (response.success && Array.isArray(response.data)) {
          setUseCases([
            { id: 'tst1', name: 'Pre-Call Summary', associated_DB: 'A3GoldDemo', domain_id: '00003' },
            { id: '00002', name: 'On Call Intelligence', associated_DB: 'A3GoldDemo', domain_id: '00003' }
          ]);
        }
      } catch (err) { console.error(err); }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await dashboardService.fetchTools();
        if (response.success && Array.isArray(response.data)) {
          setTools(response.data);
          if (response.data.length > 0) {
            const d = response.data[0];
            setNewWidget(prev => ({ ...prev, tool: d.name, toolId: d.id, endpoint: d.api_endpoint || '' }));
          }
        }
      } catch (err) { console.error(err); }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!userId) return;
      if (!isAdmin() && !isAdvisor() && !isInvestmentBanker()) return;
      try {
        setLoading(true);
        const data = await dashboardService.fetchClients(userId);
        if (data.clients && Array.isArray(data.clients)) {
          setClients(data.clients);
          if (data.clients.length > 0 && !selectedClient) {
            const matched = customerId && data.clients.find(c => c.id === customerId);
            const cust007 = data.clients.find(c => c.id === 'CUST007');
            setSelectedClient(matched || cust007 || data.clients[0]);
          }
        }
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    fetch();
  }, [userId]);

  useEffect(() => {
    if (!userId || !selectedClient || !selectedUseCase) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.fetchWidgetsAndTabs(selectedUseCase, userId);
        if (!data.success && data.error) throw new Error(data.error);
        const fetchedTabs = data.tabs || [];
        setTabs(fetchedTabs);
        if (fetchedTabs.length > 0 && !selectedTab) setSelectedTab(fetchedTabs[0].tab_id);
        const allWidgets = [];
        fetchedTabs.forEach(tab => {
          tab.widgets.forEach(w => allWidgets.push({
            id: w.widget_id, title: w.widget_name, subtitle: w.subtitle || '',
            prompt: w.prompt || '', chartPrompt: w.visual_prompt,
            customerId: selectedClient?.id, tool: w.tool_name || 'SQL Query Assistant',
            endpoint: w.endpoint || ':5009/sql-ask', content: '', status: w.status,
            hasAvatar: w.hasAvatar || false, key: `${w.widget_id}_${selectedClient?.id}`,
            tabId: tab.tab_id, promptContext: w.prompt_context || null
          }));
        });
        setWidgets(allWidgets);
        const stored = getStoredColMap();
        const colMap = {};
        fetchedTabs.forEach(tab => {
          tab.widgets.forEach((w) => {
            if (stored[w.widget_id] !== undefined) {
              colMap[w.widget_id] = stored[w.widget_id];
            }
          });
        });
        setWidgetColMap(colMap);
        // Build order: keep stored positions, append any new ids at end
        const allIds = allWidgets.map(w => w.id);
        const storedOrd = getStoredOrder().filter(id => allIds.includes(id));
        const newIds = allIds.filter(id => !storedOrd.includes(id));
        const updatedOrder = [...storedOrd, ...newIds];
        saveStoredOrder(updatedOrder);
        setWidgetOrder(updatedOrder);
        initialFetchDone.current = true;
      } catch (err) { setError(err.message); setWidgets([]); setTabs([]); }
      finally { setLoading(false); }
    };
    fetch();
  }, [userId, selectedClient, selectedUseCase]);

  const hasTranscriptionWidget = widgets.some(
    w => w.tool === 'Transcript Insight Engine' || w.tool === 'Transcript Analysis'
  );

  const handleWidgetContentChange = useCallback((widgetId, content) => {
    setWidgetContents(prev => {
      if (prev[widgetId] === content) return prev;
      return { ...prev, [widgetId]: content };
    });
  }, []);

  const handleClientChange = (client) => {
    if (client.id === selectedClient?.id) return;
    setSelectedClient(client);
    setWidgetContents({});
    setWidgets(prev => prev.map(w => ({ ...w, key: `${w.id}_${client.id}`, customerId: client.id })));
  };

  const handleUseCaseChange = (e) => {
    setSelectedUseCase(e.target.value);
    setSelectedTab(null);
    navigate(`/use-case/${e.target.value}`);
  };

  const handleTabChange = (tabId) => setSelectedTab(tabId);

  const handleAddTab = async () => {
    if (!newTabName.trim()) { alert('Please enter a tab name'); return; }
    try {
      const tabId = `tab_${Date.now().toString().slice(-6)}`;
      await dashboardService.createTab(tabId, newTabName, selectedUseCase);
      setTabs(prev => [...prev, { tab_id: tabId, tab_name: newTabName, widgets: [] }]);
      setSelectedTab(tabId);
      setNewTabName('');
      setShowTabPopup(false);
    } catch (err) { alert('Failed to create new tab.'); }
  };

  const handleDeleteTab = async (tabId) => {
    if (!tabId) return;
    if (!window.confirm('Delete this tab and all its widgets?')) return;
    try {
      await dashboardService.deleteTab(tabId);
      setTabs(prev => prev.filter(t => t.tab_id !== tabId));
      setWidgets(prev => prev.filter(w => w.tabId !== tabId));
      if (selectedTab === tabId) {
        const remaining = tabs.filter(t => t.tab_id !== tabId);
        setSelectedTab(remaining.length > 0 ? remaining[0].tab_id : null);
      }
    } catch (err) { alert('Failed to delete tab.'); }
  };

  const handleUpdateTabName = async (tabId, tabName) => {
    try {
      await dashboardService.updateTabName(tabId, tabName);
      setTabs(prev => prev.map(t => t.tab_id === tabId ? { ...t, tab_name: tabName } : t));
      return true;
    } catch (err) { return false; }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setNewWidget(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'tool') {
      const selected = tools.find(t => t.name === value);
      if (selected) setNewWidget(prev => ({ ...prev, tool: selected.name, toolId: selected.id, endpoint: selected.api_endpoint || '' }));
    } else {
      setNewWidget(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveWidget = async (combinedContext = null, ragFile = null) => {
    try {
      if (!selectedTab) { alert('Please select a tab before adding a widget'); return; }
      const widgetId = `${Date.now().toString().slice(-4)}`;
      const newWidgetObj = {
        id: widgetId, title: newWidget.title, subtitle: '', prompt: newWidget.prompt,
        chartPrompt: newWidget.widgetType === 'visualization' ? newWidget.visualPrompt : null,
        customerId: selectedClient?.id, tool: newWidget.tool,
        endpoint: newWidget.endpoint || ':5009/sql-ask', content: '',
        tabId: selectedTab, hasAvatar: newWidget.hasAvatar, promptContext: combinedContext
      };
      // Find column with shortest rendered height
      let shortestColIdx = 0;
      let minHeight = Infinity;
      for (let i = 0; i < numCols; i++) {
        const colEl = columnsRef.current[i];
        const height = colEl ? colEl.offsetHeight : Infinity;
        if (height < minHeight) { minHeight = height; shortestColIdx = i; }
      }
      saveColAssignment(widgetId, shortestColIdx);
      setWidgetColMap(prev => ({ ...prev, [widgetId]: shortestColIdx }));
      const newOrder = [...getStoredOrder(), widgetId];
      saveStoredOrder(newOrder);
      setWidgetOrder(newOrder);
      setWidgets(prev => [...prev, newWidgetObj]);

      await dashboardService.createWidget({
        id: widgetId, name: newWidget.title, tool_prompt: newWidget.prompt,
        visual_prompt: newWidget.widgetType === 'visualization' ? newWidget.visualPrompt : null,
        user_id: userId, tool_id: newWidget.toolId, use_case_id: selectedUseCase,
        tab_id: selectedTab, hasAvatar: newWidget.hasAvatar, prompt_context: combinedContext
      });

      if (ragFile && newWidget.tool === 'Retrieval Tool') {
        await dashboardService.uploadRagDocument(ragFile, `collection_${widgetId}`);
      }

      setNewWidget({
        title: '', widgetType: 'text',
        tool: tools.length > 0 ? tools[0].name : '',
        toolId: tools.length > 0 ? tools[0].id : '',
        endpoint: tools.length > 0 ? (tools[0].api_endpoint || '') : '',
        prompt: '', visualPrompt: '', tabId: selectedTab, hasAvatar: false
      });
      setShowPopup(false);
    } catch (err) { alert('Failed to save widget.'); }
  };

  const handleDeleteWidget = async (id) => {
    try {
      setWidgets(prev => prev.filter(w => w.id !== id));
      removeColAssignment(id);
      const prunedOrder = getStoredOrder().filter(wid => wid !== id);
      saveStoredOrder(prunedOrder);
      setWidgetOrder(prunedOrder);
      await dashboardService.deleteWidget(id);
    } catch (err) { console.error(err); }
  };

  const refreshWidgets = useCallback(async () => {
    if (!userId || !selectedClient || !selectedUseCase) return;
    try {
      setLoading(true);
      const data = await dashboardService.fetchWidgetsAndTabs(selectedUseCase, userId);
      if (!data.success && data.error) throw new Error(data.error);
      const fetchedTabs = data.tabs || [];
      setTabs(fetchedTabs);
      if (fetchedTabs.length > 0 && !selectedTab) setSelectedTab(fetchedTabs[0].tab_id);
      const allWidgets = [];
      fetchedTabs.forEach(tab => {
        tab.widgets.forEach(w => allWidgets.push({
          id: w.widget_id, title: w.widget_name, subtitle: w.subtitle || '',
          prompt: w.prompt || '', chartPrompt: w.visual_prompt,
          customerId: selectedClient?.id, tool: w.tool_name || 'SQL Query Assistant',
          endpoint: w.endpoint || ':5009/sql-ask', content: '', status: w.status,
          hasAvatar: w.hasAvatar || false, key: `${w.widget_id}_${selectedClient?.id}`,
          tabId: tab.tab_id, promptContext: w.prompt_context || null
        }));
      });
      setWidgets(allWidgets);
      const stored = getStoredColMap();
      const colMap = {};
      fetchedTabs.forEach(tab => {
        tab.widgets.forEach((w) => {
          if (stored[w.widget_id] !== undefined) {
            colMap[w.widget_id] = stored[w.widget_id];
          }
        });
      });
      setWidgetColMap(colMap);
      const allIds = allWidgets.map(w => w.id);
      const storedOrd = getStoredOrder().filter(id => allIds.includes(id));
      const newIds = allIds.filter(id => !storedOrd.includes(id));
      const updatedOrder = [...storedOrd, ...newIds];
      saveStoredOrder(updatedOrder);
      setWidgetOrder(updatedOrder);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [userId, selectedClient, selectedUseCase, selectedTab]);

  // Audit modal
  const handleShowAuditModal = (data) => {
    setAuditModalData(data);
    setShowAuditModal(true);
    if (!data.loading) {
      setActiveModalTab('audit');
      setGraphData(null);
    }
  };

  const fetchAgentGraph = async (uid) => {
    if (!uid) return;
    setIsLoadingGraph(true);
    setGraphError(null);
    try {
      const gd = await dashboardService.getAgentGraph(uid);
      setGraphData(gd);
    } catch (err) { setGraphError('Failed to load agent workflow graph.'); }
    finally { setIsLoadingGraph(false); }
  };

  const handleAuditModalTabChange = async (tab) => {
    setActiveModalTab(tab);
    if (tab === 'lineage' && auditModalData.tool === 'NOVA' && !graphData) {
      await fetchAgentGraph(auditModalData.userId);
    }
  };

  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setActiveModalTab('audit');
  };

  // PPT Export
  const cleanContent = (content) => {
    if (!content) return '';
    if (typeof content !== 'string') return String(content);
    try {
      const stripped = content.replace(/```(?:json|javascript|js)?\s*|\s*```/g, '').trim();
      const parsed = JSON.parse(stripped);
      const extractText = (obj) => {
        if (typeof obj === 'string') return obj;
        if (typeof obj === 'number') return String(obj);
        if (Array.isArray(obj)) return obj.map(extractText).join(', ');
        if (typeof obj === 'object' && obj !== null) return Object.entries(obj).map(([k, v]) => `${k}: ${extractText(v)}`).join('; ');
        return '';
      };
      return extractText(parsed);
    } catch (e) {
      return content
        .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
        .replace(/<p[^>]*>/gi, '').replace(/<\/p>/gi, '\n')
        .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
        .replace(/<ul[^>]*>|<\/ul>|<ol[^>]*>|<\/ol>/gi, '\n')
        .replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
        .replace(/\n{3,}/g, '\n\n').trim();
    }
  };

  const cleanedWidgetContents = useMemo(() => {
    return Object.fromEntries(Object.entries(widgetContents).map(([id, c]) => [id, cleanContent(c)]));
  }, [widgetContents]);

  const handleExportToPPT = async () => {
    try {
      setExportLoading(true);
      const slidesData = [];
      if (selectedClient) {
        slidesData.push({
          title: `${selectedClient.name} - Client Profile`, type: 'text',
          data: `Client: ${selectedClient.name}\nAUM: ${selectedClient.total_aum || 'N/A'}\nYTD Return: ${selectedClient.ytd_return || 'N/A'}`
        });
      }
      const currentTabWidgets = widgets.filter(w => w.tabId === selectedTab);
      for (const widget of currentTabWidgets) {
        const wContent = widgetContents[widget.id];
        if (!wContent) continue;
        const cleanedText = typeof wContent === 'string' ? wContent.replace(/<[^>]*>/g, '').replace(/\n{3,}/g, '\n\n').trim() : String(wContent);
        slidesData.push({ title: widget.title || `Widget ${widget.id}`, type: 'text', data: cleanedText });
      }
      if (slidesData.length === 0) { alert('No content available to export.'); return; }
      const blob = await dashboardService.exportToPPT({
        slides: slidesData, logo: PPT_LOGO, background: PPT_BACKGROUND, has_dark_background: true
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientName = selectedClient ? selectedClient.name.replace(/\s+/g, '_') : 'client';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      link.setAttribute('download', `${clientName}_report_${timestamp}.pptx`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); link.remove(); }, 100);
    } catch (err) { alert(`Failed to export: ${err.message}`); }
    finally { setExportLoading(false); }
  };

  const renderFormattedAudit = (text) => {
    if (!text) return <p className="text-gray-500 italic">No audit information available</p>;
    const sections = text.split(/(?=### )/);
    return (
      <div className="text-gray-700 audit-container p-4 bg-gray-50 rounded-lg">
        {sections.map((section, si) => {
          if (!section.trim()) return null;
          const lines = section.split('\n');
          const headerMatch = lines[0].match(/^### (.+)$/);
          if (!headerMatch) return <div key={si} className="mb-4"><p>{section}</p></div>;
          const sectionTitle = headerMatch[1];
          const sectionContent = lines.slice(1).join('\n').trim();
          return (
            <div key={si} className="mb-6">
              <h3 className="text-lg font-bold text-primary mb-2">{sectionTitle}</h3>
              <div className="pl-2 border-l-2 border-primaryLight">
                <div className="text-sm">
                  {sectionContent.split('\n').map((line, li) => {
                    if (!line.trim()) return null;
                    if (line.trim().startsWith('-')) {
                      return (
                        <div key={li} className="flex mb-1">
                          <span className="mr-2">•</span>
                          <span dangerouslySetInnerHTML={{ __html: line.replace(/^-\s*/, '').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }} />
                        </div>
                      );
                    }
                    return <p key={li} className="mb-1" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }} />;
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const breakpointColumnsObj = { default: 3, 1280: 3, 1024: 2, 640: 1 };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left panel — sticky client profile */}
        <aside className={`fixed inset-y-0 left-0 z-40 w-64 lg:relative lg:z-auto lg:w-[200px] xl:w-[15%] xl:min-w-[200px] h-full overflow-y-auto bg-white border-r border-gray-100 shrink-0 transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <SlimClientProfile
            clientData={selectedClient}
            allClients={clients}
            onClientChange={handleClientChange}
            useCaseId={selectedUseCase}
            widgetContents={cleanedWidgetContents}
            allWidgetsLoaded={allWidgetsLoaded}
            onNavigateBack={() => navigate('/home')}
            useCases={useCases}
            selectedUseCase={selectedUseCase}
            onUseCaseChange={handleUseCaseChange}
            dataLoading={loading}
          />
        </aside>

        {/* Right panel */}
        <main className="flex-1 flex flex-col h-full overflow-y-auto">


          {/* Tabs + Actions */}
          <div className="px-3 sm:px-5 pt-1 border-b border-gray-100 bg-white shrink-0 flex items-center justify-between">
            <button
              className="lg:hidden p-1.5 mr-2 rounded-md text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
              onClick={() => setSidebarOpen(v => !v)}
              title="Toggle sidebar"
            >
              <Menu size={18} />
            </button>
            <div className="flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <TabNavigation
                tabs={tabs}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
                onTabDelete={handleDeleteTab}
                onTabNameUpdate={handleUpdateTabName}
                onAddTabClick={() => setShowTabPopup(true)}
              />
            </div>
            <div className="flex items-center gap-3 ml-4 shrink-0 pb-1">
              <ActionsMenu
                onExportPPT={handleExportToPPT}
                exportLoading={exportLoading}
                onAddWidget={() => setShowPopup(true)}
                selectedTab={selectedTab}
                widgetContents={widgetContents}
              />
              {/* <img src="/capgemini-logo.png" alt="Capgemini" className="h-6 w-auto object-contain" /> */}
            </div>
          </div>

          {/* Transcription widget */}
          {hasTranscriptionWidget && (
            <div className="mx-5 mt-4 rounded-lg">
              <TranscriptionWidget onTranscriptionComplete={() => { }} />
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-secondary border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          )}

          {/* Widgets */}
          {!loading && selectedTab ? (
            <div className="flex items-start gap-5 p-5 mb-6">
              {Array.from({ length: numCols }, (_, colIdx) => {
                const tabWidgets = widgets.filter(w => w.tabId === selectedTab);
                const colWidgets = tabWidgets
                  .filter((w) => {
                    if (widgetColMap[w.id] !== undefined) return widgetColMap[w.id] === colIdx;
                    const orderIdx = widgetOrder.indexOf(w.id);
                    const pos = orderIdx >= 0 ? orderIdx : tabWidgets.indexOf(w);
                    return pos % numCols === colIdx;
                  })
                  .sort((a, b) => {
                    const ai = widgetOrder.indexOf(a.id);
                    const bi = widgetOrder.indexOf(b.id);
                    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
                  });
                return (
                  <div
                    key={colIdx}
                    className="flex-1 flex flex-col gap-5"
                    ref={el => { columnsRef.current[colIdx] = el; }}
                  >
                    {colWidgets.map(widget => (
                      <div key={widget.key || widget.id} id={`widget-${widget.id}`}>
                        <Widget
                          id={widget.id}
                          title={widget.title}
                          subtitle={widget.subtitle}
                          prompt={widget.prompt}
                          chartPrompt={widget.chartPrompt || null}
                          customerId={selectedClient?.id || widget.customerId}
                          tool={widget.tool}
                          endpoint={widget.endpoint}
                          content={widget.content}
                          status={widget.status}
                          hasAvatar={widget.hasAvatar}
                          onContentChange={handleWidgetContentChange}
                          onDelete={handleDeleteWidget}
                          useCaseId={selectedUseCase}
                          promptContext={widget.promptContext}
                          collection={`collection_${widget.id}`}
                          onShowAuditModal={handleShowAuditModal}
                          onLoadingChange={(isLoading) => handleWidgetLoadingChange(widget.id, isLoading)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : !loading && (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              {tabs.length === 0 ? (
                <>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 shadow-[0_4px_6px_rgba(0,0,0,0.07)]" style={{ background: 'linear-gradient(135deg, var(--color-primary)/10, var(--color-secondary)/10)', backgroundColor: '#f0f4ff' }}>
                    <Plus size={28} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#374151] mb-1.5">No tabs available</h3>
                  <p className="text-sm text-gray-500 mb-5" style={{ lineHeight: '1.6' }}>Create a new tab to get started</p>
                  <button
                    onClick={() => setShowTabPopup(true)}
                    className="px-5 py-2.5 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_6px_16px_rgba(0,0,0,0.15)]"
                    style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
                  >
                    Create New Tab
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: '#f0f4ff' }}>
                    <Plus size={28} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#374151] mb-1.5">No tab selected</h3>
                  <p className="text-sm text-gray-500" style={{ lineHeight: '1.6' }}>Select a tab to view its widgets</p>
                </>
              )}
            </div>
          )}
        </main>

      <WealthChatBot
        cust_id={selectedClient?.id}
        use_case_id={selectedUseCase}
        tab_id={selectedTab}
        advisor_id={userId}
        onWidgetCreated={refreshWidgets}
      />

      {showPopup && (
        <AddWidgetModal
          newWidget={newWidget}
          handleInputChange={handleInputChange}
          handleSaveWidget={handleSaveWidget}
          onClose={() => setShowPopup(false)}
          tools={tools}
          tabs={tabs}
          existingWidgets={widgets.filter(w => w.tabId === selectedTab)}
          widgetContents={widgetContents}
        />
      )}

      {showTabPopup && (
        <AddTabModal
          newTabName={newTabName}
          setNewTabName={setNewTabName}
          handleAddTab={handleAddTab}
          onClose={() => setShowTabPopup(false)}
        />
      )}

      {showAuditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/30">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" style={{ borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.07)' }}>
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex space-x-2">
                {auditModalData.tool === 'NOVA' ? (
                  <div className="flex bg-gray-100 p-1 rounded-xl overflow-hidden gap-1">
                    <button
                      onClick={() => handleAuditModalTabChange('audit')}
                      className={`px-4 py-1.5 text-sm rounded-lg transition-all duration-300 font-medium ${activeModalTab === 'audit' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Audit Summary
                    </button>
                    <button
                      onClick={() => handleAuditModalTabChange('lineage')}
                      className={`px-4 py-1.5 text-sm rounded-lg transition-all duration-300 font-medium ${activeModalTab === 'lineage' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Agent Lineage
                    </button>
                  </div>
                ) : (
                  <h2 className="text-base font-semibold text-[#374151]">Audit Summary</h2>
                )}
              </div>
              <button onClick={handleCloseAuditModal} className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-lg transition-all duration-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto p-5 flex-grow" style={{ backgroundColor: '#f8f9fc' }}>
              {activeModalTab === 'audit' ? (
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_6px_rgba(0,0,0,0.07)]">
                  {auditModalData.loading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-gray-500">Loading audit summary…</p>
                    </div>
                  ) : renderFormattedAudit(auditModalData.content)}
                </div>
              ) : (
                isLoadingGraph ? (
                  <div className="flex justify-center items-center py-10 gap-2">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-gray-500">Loading agent workflow graph...</span>
                  </div>
                ) : graphError ? (
                  <div className="bg-red-50 text-red-600 p-5 rounded-2xl border border-red-100">
                    <p className="font-semibold mb-1">Error loading workflow:</p>
                    <p className="text-sm">{graphError}</p>
                  </div>
                ) : !graphData ? (
                  <div className="flex justify-center items-center h-[400px] bg-white rounded-2xl border border-gray-100 shadow-[0_4px_6px_rgba(0,0,0,0.07)]">
                    <p className="text-gray-400 text-center p-6">No workflow data available yet.</p>
                  </div>
                ) : (
                  <div className="w-full h-[500px] border border-gray-100 rounded-2xl shadow-[0_4px_6px_rgba(0,0,0,0.07)] bg-white">
                    <WorkflowVisualizer graphData={graphData} />
                  </div>
                )
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleCloseAuditModal}
                className="px-5 py-2 text-sm text-white rounded-xl transition-all duration-300 hover:opacity-90 hover:scale-[1.02] bg-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard2;
